/**
 * Abrir no IW a admissão do Caso vinculado (NR-256). O back enfileira e faz a primeira tentativa na hora: a resposta
 * já traz o desfecho e o detalhe atualizado. O ator NÃO vai no corpo: o back o lê da sessão.
 */

import { queryOptions } from '@tanstack/react-query';
import type { IwAdmissionGroupsResponse, OpenIwAdmissionRequest, OpenIwAdmissionResponse } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { FUSO_DE_EXIBICAO } from '../../../shared/dominio/datas';
import type { CampoRecusadoPeloIw } from './cadastroNoIw';
import { mapearCasoDetalhado } from './mapeadores';
import type { CasoDetalhado } from './tipos';

export type DesfechoDaAdmissao = 'Opened' | 'Pending' | 'Refused' | 'Unanswered' | 'Desconhecido';

export interface ResultadoDaAdmissaoNoIw {
  readonly desfecho: DesfechoDaAdmissao;
  readonly idAdmissao: string | null;
  readonly camposRecusados: readonly CampoRecusadoPeloIw[];
  readonly motivo: string | null;
  /** A recusa é da plataforma (paciente em atendimento, vínculo perdido...), não do IW. */
  readonly recusadoPelaPlataforma: boolean;
  readonly caso: CasoDetalhado;
}

const DESFECHOS: readonly string[] = ['Opened', 'Pending', 'Refused', 'Unanswered'];

export function mapearResultadoDaAdmissao(resposta: OpenIwAdmissionResponse): ResultadoDaAdmissaoNoIw {
  const desfecho = resposta.outcome ?? '';
  return {
    desfecho: DESFECHOS.includes(desfecho) ? (desfecho as DesfechoDaAdmissao) : 'Desconhecido',
    idAdmissao: resposta.admissionId ?? null,
    camposRecusados: (resposta.refusedFields ?? []).flatMap((c): CampoRecusadoPeloIw[] =>
      c.field ? [{ campo: c.field, mensagem: c.message ?? '' }] : [],
    ),
    motivo: resposta.reason ?? null,
    recusadoPelaPlataforma: resposta.refusedByPlatform,
    caso: mapearCasoDetalhado(resposta.case),
  };
}

/** Um grupo do cadastro do IW que a pessoa escolhe ao abrir a admissão (NR-262, TP-068). */
export interface GrupoDoIw {
  readonly codigo: number;
  readonly nome: string;
  /** Só no de dispensação: o mesmo nome se repete em filiais. */
  readonly filial: string | null;
  /** Filial inativa ainda é aceita pelo IW; a tela avisa. */
  readonly filialAtiva: boolean;
}

export interface GruposDaAdmissao {
  readonly dispensacao: readonly GrupoDoIw[];
  /** A rota de entrega, por região e dia da semana. */
  readonly entrega: readonly GrupoDoIw[];
  readonly padraoDispensacao: number | null;
  readonly padraoEntrega: number | null;
}

export interface GruposEscolhidos {
  readonly dispensacao: number;
  readonly entrega: number;
  /** A data de internação que a pessoa informou (NR-261), `yyyy-MM-dd`. Obrigatória. */
  readonly dataInternacao: string;
}

export function rotuloDoGrupo(grupo: GrupoDoIw): string {
  // Sem nome, o código já está no "Grupo N": repetir daria "3 · Grupo 3".
  const partes = grupo.nome === '' ? [`Grupo ${String(grupo.codigo)}`] : [String(grupo.codigo), grupo.nome];
  if (grupo.filial !== null) partes.push(grupo.filial);
  const rotulo = partes.join(' · ');
  return grupo.filialAtiva ? rotulo : `${rotulo} (filial inativa)`;
}

function mapearGrupos(resposta: IwAdmissionGroupsResponse): GruposDaAdmissao {
  const grupo = (g: { code: number; name: string | null; branch?: string | null; branchActive: boolean }): GrupoDoIw => ({
    codigo: g.code,
    nome: g.name ?? '',
    filial: g.branch ?? null,
    filialAtiva: g.branchActive,
  });
  return {
    dispensacao: (resposta.dispensingGroups ?? []).map(grupo),
    entrega: (resposta.deliveryGroups ?? []).map(grupo),
    padraoDispensacao: resposta.defaultDispensingGroupId ?? null,
    padraoEntrega: resposta.defaultDeliveryGroupId ?? null,
  };
}

/** `GET /iw/admissao/grupos`: o cadastro vivo do IW, com 5 min de cache na API (TP-068). */
export function opcoesDosGruposDaAdmissao() {
  return queryOptions({
    queryKey: ['iw', 'admissao', 'grupos'] as const,
    queryFn: ({ signal }) =>
      requisitar<IwAdmissionGroupsResponse>('/iw/admissao/grupos', { sinal: signal }).then(mapearGrupos),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Dias antes e depois de hoje (Brasília) aceitos para a data de internação: a mesma faixa do back (TP-062 T15). */
export const DIAS_ANTES_DA_INTERNACAO = 90;
export const DIAS_DEPOIS_DA_INTERNACAO = 90;

function somarDias(dia: string, dias: number): string {
  const data = new Date(`${dia}T00:00:00Z`);
  data.setUTCDate(data.getUTCDate() + dias);
  return data.toISOString().slice(0, 10);
}

/**
 * A faixa da data de internação (`yyyy-MM-dd`), contada do dia de hoje em Brasília como no back: o campo a usa em
 * `min`/`max`, e a confirmação só habilita dentro dela. A data não é editável depois da abertura.
 */
export function faixaDaDataDeInternacao(agora: Date = new Date()): { readonly minima: string; readonly maxima: string } {
  const hoje = new Intl.DateTimeFormat('en-CA', { timeZone: FUSO_DE_EXIBICAO, year: 'numeric', month: '2-digit', day: '2-digit' }).format(agora);
  return { minima: somarDias(hoje, -DIAS_ANTES_DA_INTERNACAO), maxima: somarDias(hoje, DIAS_DEPOIS_DA_INTERNACAO) };
}

/** `2026-10-20` → `20/10/2026`. */
export function formatarDiaDaInternacao(dia: string): string {
  const [ano, mes, d] = dia.split('-');
  return `${d ?? ''}/${mes ?? ''}/${ano ?? ''}`;
}

/**
 * `POST /casos/{id}/admissao-iw`, com os grupos e a data de internação que a pessoa informou. Sem vínculo, já aberta,
 * desligada ou já na fila com outros dados: `409`; data ausente ou fora da faixa: `400`; grupo fora da lista ou sem as
 * listas na configuração: `422`.
 */
export function abrirAdmissaoNoIw(id: string, grupos: GruposEscolhidos, sinal?: AbortSignal): Promise<ResultadoDaAdmissaoNoIw> {
  const corpo: OpenIwAdmissionRequest = {
    dispensingGroupId: grupos.dispensacao,
    deliveryGroupId: grupos.entrega,
    hospitalizationDate: grupos.dataInternacao,
  };
  return requisitar<OpenIwAdmissionResponse>(`/casos/${encodeURIComponent(id)}/admissao-iw`, {
    method: 'POST',
    corpo,
    sinal,
  }).then(mapearResultadoDaAdmissao);
}
